import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MyOrganisationService } from '@services/organisations/my-organisation.service';
import { MessengerService } from '@services/messenger/messenger.service';
import { StorageService } from '@services/storage/storage.service';
import { Router } from '@angular/router';
import { Models } from '@ibexfusion/models';
import * as _ from 'lodash';
import { BookmarkedOrganisationsService } from '@services/bookmarked-organisations/bookmarked-organisations.service';

@Component({
  selector: 'app-select-organisation',
  templateUrl: './select-organisation.component.html',
  styleUrls: ['./select-organisation.component.scss'],
  providers: [MyOrganisationService, BookmarkedOrganisationsService],
  standalone: false
})
export class SelectOrganisationComponent implements OnInit, OnDestroy {

  @ViewChild('searchField') searchField: ElementRef;

  myOrganisations: Models.MyOrganisation[];
  filteredOrganisations: Models.MyOrganisation[];
  bookmarks: Models.BookmarkedOrganisations[];
  sortedOrganisations = {};
  childOrganisations = [];
  organisationTags = ['static', 'destination', 'accommodation', 'tourism', 'finance', 'health', 'government', 'culture', 'other', 'internal', 'none'];
  currentSearchTerm: string;
  supportMail = 'support@ipeak.ch';

  constructor(
    private myOrganisationService: MyOrganisationService, private messengerService: MessengerService, private storage: StorageService, private router: Router,
    private bookmarkService: BookmarkedOrganisationsService
  ) {
  }

  async ngOnInit() {
    const result = await this.myOrganisationService.find(0, 1000);
    if (!result?.data?.length) {
      console.error('you have no permissions in no organisations');
      return;
    }
    await this.getBookmarked();
    if (result.data.length <= 15) {
      this.selectOrganisation(result.data[0]);
    } else {
      this.myOrganisations = result.data;
      const value = await this.storage.getData('organisation-search-value');
      if (value?.value?.length) {
        this.currentSearchTerm = value.value;
        this.filterOrganisations(this.currentSearchTerm);
      } else {
        this.filteredOrganisations = this.myOrganisations;
        this.sortOrganisationsByTag(true);
      }
    }
  }

  ngOnDestroy() {
    this.storage.setData('organisation-search-value', { value: this.currentSearchTerm });
  }

  sortOrganisationsByTag(initial = false) {
    this.filteredOrganisations = _.orderBy(this.filteredOrganisations, [(org) => org.title.toLowerCase()], 'asc');

    this.organisationTags.map(tag => this.sortedOrganisations[tag] = []);
    this.sortedOrganisations['bookmarked'] = [];
    this.filteredOrganisations.map(org => {
      if (org.ParentOrganisationID && _.find(this.myOrganisations, myOrg => myOrg.OrganisationID === org.ParentOrganisationID)) {
        this.childOrganisations.push(org); // we are assigned to the parent Organisation too - skip it and display select on the org-dashboard
        return;
      }
      if (!!(this.bookmarks || []).find(b => b.organisationId === org.OrganisationID)) {
        this.sortedOrganisations['bookmarked'].push(org);
      } else if (org['tag'] && this.sortedOrganisations[org['tag']]) {
        this.sortedOrganisations[org['tag']].push(org);
      } else {
        this.sortedOrganisations['none'].push(org);
      }
    });
    if (initial && this.filteredOrganisations.length === 1) { // we are only assigned to one parent Organisation
      return this.selectOrganisation(this.filteredOrganisations[0]);
    }
    let count = 0;
    let org = 0;
    Object.keys(this.sortedOrganisations).forEach(k => {
      count += this.sortedOrganisations[k]?.length;
      org = org || this.sortedOrganisations[k]?.[0];
    });
    if (initial && count === 1 && !!org) {
      return this.selectOrganisation(org);
    }
    // todo: what if we are in multiple child-organisations but not in the parent.
  }

  filterOrganisations(searchTerm: string) {
    this.currentSearchTerm = searchTerm;
    this.storage.setData('organisation-search-value', { value: searchTerm });
    if (this.searchField && this.searchField.nativeElement && typeof this.searchField.nativeElement.value === 'string') {
      searchTerm = this.searchField.nativeElement.value;
    }
    let subQuery = <any>{ $like: '%' + searchTerm + '%' };

    /* multi term query if we have pipe-char */
    const searchTerms = searchTerm.split('|');
    if (searchTerms && searchTerms.length > 1) {
      subQuery = { $or: [] };
      searchTerms.map(term => {
        if (term && term.length && _.trim(term).length) {
          subQuery.$or.push({ $like: '%' + _.trim(term) + '%' });
        }
      });
    }
    this.myOrganisationService.find(0, 1000, { $or: [{ name: subQuery }, { title: subQuery }] }).then(result => {
      if (result && result.data) {
        this.filteredOrganisations = result.data;
        this.sortOrganisationsByTag();
      }
    });
  }

  selectOrganisation(org) {
    this.messengerService.setOrganisation(org);
    setTimeout(() => {
      this.router.navigate([{ outlets: { primary: 'dashboard', org: org.name } }])
    }, 0);
  }

  getIcon(organisation: any) {
    switch (organisation?.tag) {
      case 'destination':
        return 'explore';
      case 'accommodation':
        return 'hotel';
      case 'health':
        return 'local_hospital';
      case 'finance':
        return 'attach_money';
      case 'government':
        return 'account_balance';
      case 'culture':
        return 'comedy_mask';
      default:
        return 'business';
    }
  }

  async getBookmarked() {
    let bookmarks = { data: [] };
    try {
      bookmarks = await this.bookmarkService.find(0, 1000, { userId: this.messengerService.$activeProfile.getValue().ProfileID });
    } catch (err) {
      console.warn(err);
    }
    this.bookmarks = bookmarks?.data;
  }

  async toggleBookmark(organisation: any, event: any) {
    event?.stopPropagation?.();
    const profileId = this.messengerService.$activeProfile.getValue().ProfileID;
    const bookmark = (this.bookmarks || []).find(b => b.organisationId === organisation.OrganisationID);
    if (bookmark) {
      await this.bookmarkService.remove(bookmark._id);
    } else {
      await this.bookmarkService.create({ organisationId: organisation.OrganisationID, userId: profileId });
    }
    await this.getBookmarked();
    this.sortOrganisationsByTag();
  }

  isBookmarked(organisation: any) {
    return !!(this.bookmarks || []).find(b => b.organisationId === organisation.OrganisationID);
  }
}
